import type { ProjectFile } from '@shared/models/project-file-batch.model';
import {
  isProjectFileExtractionOutputV1,
  type ProjectFileExtractionOutput,
} from '@shared/models/project-file-extraction.model';

const nonEmpty = (v: unknown): string | null =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;

/**
 * Prefer title-block `project_title`, else `project_type · location` from
 * `project_description` segments. Description from `building_description`.
 */
export const getSuggestedProjectLabelsFromExtractions = (
  extractionOutputsOrdered: readonly (
    ProjectFileExtractionOutput | null | undefined
  )[],
): {
  suggestedProjectName: string | null;
  suggestedProjectDescription: string | null;
} => {
  let nameFromStamp: string | null = null;
  let nameFromDescription: string | null = null;
  let description: string | null = null;

  for (const raw of extractionOutputsOrdered) {
    if (raw == null || !isProjectFileExtractionOutputV1(raw)) continue;
    for (const seg of raw.segments) {
      if (!seg.ok || seg.data === undefined || seg.data === null) continue;
      const d = seg.data as Record<string, unknown>;
      if (seg.classificationType === 'title_block') {
        const t = nonEmpty(d.project_title);
        if (t !== null && nameFromStamp === null) nameFromStamp = t;
      }
      if (seg.classificationType === 'project_description') {
        const bd = nonEmpty(d.building_description);
        if (bd !== null && description === null) description = bd;
        const pt = nonEmpty(d.project_type);
        const loc = nonEmpty(d.location);
        const parts = [pt, loc].filter((x): x is string => x !== null);
        if (parts.length > 0 && nameFromDescription === null)
          nameFromDescription = parts.join(' · ');
      }
    }
  }

  return {
    suggestedProjectName: nameFromStamp ?? nameFromDescription,
    suggestedProjectDescription: description,
  };
};

/** One hint row for heuristic merge or AI batch consolidation prompt. */
export type ProjectLabelCandidate = {
  value: string;
  source: 'classifier' | 'extraction';
  /** Order after sorting by {@link ProjectFile.updatedAt}. */
  fileOrder: number;
  fileName: string;
  documentKind: string | null;
};

const sameLower = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

const toUpdatedAtMillis = (value: ProjectFile['updatedAt']): number => {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Deterministic pick from several non-empty labels: dedupe case-insensitive,
 * then prefer classifier-sourced rows; tie-break longer string, then earlier
 * `fileOrder`.
 */
export const consolidateProjectLabelCandidates = (
  candidates: readonly ProjectLabelCandidate[],
): string | null => {
  const seen = new Set<string>();
  const deduped: ProjectLabelCandidate[] = [];
  for (const c of candidates) {
    const t = c.value.trim();
    if (t.length === 0) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push({ ...c, value: t });
  }
  if (deduped.length === 0) return null;
  if (deduped.length === 1) {
    const sole = deduped[0];
    return sole === undefined ? null : sole.value;
  }

  const classifiers = deduped.filter((c) => c.source === 'classifier');
  const pool = classifiers.length > 0 ? classifiers : deduped;

  const sorted = [...pool].sort((a, b) => {
    if (b.value.length !== a.value.length)
      return b.value.length - a.value.length;
    return a.fileOrder - b.fileOrder;
  });
  return sorted[0]?.value ?? null;
};

export const collectProjectNameHints = (
  files: readonly Pick<
    ProjectFile,
    | 'extractionOutput'
    | 'updatedAt'
    | 'classifierSuggestedProjectName'
    | 'fileName'
    | 'classifierDocumentKind'
  >[],
): ProjectLabelCandidate[] => {
  const ordered = [...files].sort(
    (a, b) => toUpdatedAtMillis(a.updatedAt) - toUpdatedAtMillis(b.updatedAt),
  );
  const out: ProjectLabelCandidate[] = [];
  ordered.forEach((f, fileOrder) => {
    const docKind = f.classifierDocumentKind ?? null;
    const fromClassifier = nonEmpty(
      f.classifierSuggestedProjectName ?? undefined,
    );
    const fromExtraction = getSuggestedProjectLabelsFromExtractions([
      f.extractionOutput,
    ]).suggestedProjectName;

    if (fromClassifier !== null) {
      out.push({
        value: fromClassifier,
        source: 'classifier',
        fileOrder,
        fileName: f.fileName,
        documentKind: docKind,
      });
    }
    const ext = nonEmpty(fromExtraction ?? undefined);
    if (
      ext !== null &&
      (fromClassifier === null || !sameLower(ext, fromClassifier))
    ) {
      out.push({
        value: ext,
        source: 'extraction',
        fileOrder,
        fileName: f.fileName,
        documentKind: docKind,
      });
    }
  });
  return out;
};

export const collectProjectDescriptionHints = (
  files: readonly Pick<
    ProjectFile,
    | 'extractionOutput'
    | 'updatedAt'
    | 'classifierSuggestedProjectDescription'
    | 'fileName'
    | 'classifierDocumentKind'
  >[],
): ProjectLabelCandidate[] => {
  const ordered = [...files].sort(
    (a, b) => toUpdatedAtMillis(a.updatedAt) - toUpdatedAtMillis(b.updatedAt),
  );
  const out: ProjectLabelCandidate[] = [];
  ordered.forEach((f, fileOrder) => {
    const docKind = f.classifierDocumentKind ?? null;
    const fromClassifier = nonEmpty(
      f.classifierSuggestedProjectDescription ?? undefined,
    );
    const fromExtraction = getSuggestedProjectLabelsFromExtractions([
      f.extractionOutput,
    ]).suggestedProjectDescription;

    if (fromClassifier !== null) {
      out.push({
        value: fromClassifier,
        source: 'classifier',
        fileOrder,
        fileName: f.fileName,
        documentKind: docKind,
      });
    }
    const ext = nonEmpty(fromExtraction ?? undefined);
    if (
      ext !== null &&
      (fromClassifier === null || !sameLower(ext, fromClassifier))
    ) {
      out.push({
        value: ext,
        source: 'extraction',
        fileOrder,
        fileName: f.fileName,
        documentKind: docKind,
      });
    }
  });
  return out;
};

/**
 * Heuristic merge for listings (no AI): same tier rules as consolidation
 * fallback — dedupe; prefer classifier; longest; earlier `updatedAt`.
 */
export const getSuggestedProjectLabelsFromProjectFiles = (
  files: readonly Pick<
    ProjectFile,
    | 'extractionOutput'
    | 'updatedAt'
    | 'classifierSuggestedProjectName'
    | 'classifierSuggestedProjectDescription'
    | 'fileName'
    | 'classifierDocumentKind'
  >[],
): {
  suggestedProjectName: string | null;
  suggestedProjectDescription: string | null;
} => ({
  suggestedProjectName: consolidateProjectLabelCandidates(
    collectProjectNameHints(files),
  ),
  suggestedProjectDescription: consolidateProjectLabelCandidates(
    collectProjectDescriptionHints(files),
  ),
});
